import { FormlyFieldConfig } from '@ngx-formly/core';
import { formCondition } from '@intellio/shared/utils';

export const evaluateFormlyConditions = (
  field: FormlyFieldConfig,
  root: FormlyFieldConfig
): boolean => {
  const conditions = field.props?.condition ?? [];

  if (conditions.length === 0) {
    return true;
  }

  if (field.props?.conditionOptions?.operator === '||') {
    return conditions.some((condition) =>
      formCondition(condition, field, root)
    );
  }

  return conditions.every((condition) => formCondition(condition, field, root));
};

// A field can be turned off by a `feature-flag` wrapper that shares the same
// field config (or sits on an ancestor section). When that happens the wrapper
// disables the control and sets `props.featureFlagHidden`. The conditional
// enable logic must respect that flag, otherwise a feature-flag-disabled field
// gets re-enabled the moment its own condition turns true — leaving a hidden,
// required control that fails validation on submit even though the user can
// never see or fill it.
export const isFeatureFlagHidden = (field: FormlyFieldConfig): boolean =>
  field?.props?.featureFlagHidden === true;

export const isConditionallyHiddenByAncestor = (
  field: FormlyFieldConfig,
  root: FormlyFieldConfig
): boolean => {
  let current = field.parent;

  while (current) {
    if (isFeatureFlagHidden(current)) {
      return true;
    }

    const isConditional =
      current.wrappers?.includes('conditional') ||
      !!current.props?.condition;

    if (isConditional) {
      if (current.formControl?.disabled) {
        return true;
      }

      if (!evaluateFormlyConditions(current, root)) {
        return true;
      }
    }

    current = current.parent;
  }

  return false;
};

export const shouldEnableFormlyFieldControls = (
  field: FormlyFieldConfig,
  root: FormlyFieldConfig
): boolean => {
  return (
    !isFeatureFlagHidden(field) &&
    evaluateFormlyConditions(field, root) &&
    !isConditionallyHiddenByAncestor(field, root)
  );
};

export const shouldEnableFieldFromParentCascade = (
  field: FormlyFieldConfig,
  root: FormlyFieldConfig
): boolean => {
  const hasConditionalWrapper =
    field.wrappers?.includes('conditional');

  return hasConditionalWrapper
    ? shouldEnableFormlyFieldControls(field, root)
    : true;
};
